import type {
  CreateLockerverseWidgetOptions,
  LockerverseCatalog,
  LockerverseCheckoutClient,
} from "@lockerverse/sdk/checkout";
import { createLockerverseCheckoutClient } from "@lockerverse/sdk/checkout";
import { useCallback, useEffect, useRef, useState } from "react";
import { useImmutableResourceIdentity } from "./immutable-resource-identity.ts";

export type UseLockerverseWidgetResult = {
  catalog: LockerverseCatalog | null;
  client: LockerverseCheckoutClient;
  error: Error | null;
  loading: boolean;
  refresh: () => void;
};

type WidgetLoadState = {
  catalog: LockerverseCatalog | null;
  error: Error | null;
};

function toError<ErrorValue>(error: ErrorValue) {
  return error instanceof Error
    ? error
    : new Error("Unable to load the Lockerverse widget.");
}

export function useLockerverseWidget(
  options: CreateLockerverseWidgetOptions
): UseLockerverseWidgetResult {
  const { apiBaseUrl, communitySlug, environment, onError, widgetSlug } =
    options;
  useImmutableResourceIdentity("payment widget", {
    apiBaseUrl,
    communitySlug,
    environment,
    slug: widgetSlug,
  });
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<WidgetLoadState>({
    catalog: null,
    error: null,
  });
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const [client] = useState(() =>
    createLockerverseCheckoutClient({
      ...options,
      onError: (reportedError) => onErrorRef.current?.(reportedError),
    })
  );

  useEffect(() => {
    let active = true;

    async function loadWidget() {
      try {
        const catalog = await client.load();
        if (active) {
          setState({
            catalog,
            error: null,
          });
        }
      } catch (cause) {
        if (active) {
          setState({
            catalog: null,
            error: toError(cause),
          });
        }
      }
    }

    loadWidget();
    return () => {
      active = false;
    };
  }, [client, revision]);

  const refresh = useCallback(() => {
    setState({
      catalog: null,
      error: null,
    });
    setRevision((value) => value + 1);
  }, []);
  const { catalog, error: loadError } = state;

  return {
    catalog,
    client,
    error: loadError,
    loading: catalog === null && loadError === null,
    refresh,
  };
}
